import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Loader2, Plus, X } from "lucide-react"
import { useState } from "react"
import { useSearchParams } from "react-router"
import {
  useCustomerProductMappings,
  useDeleteCustomerProductMapping,
  useUpsertCustomerProductMapping,
  type CustomerProductMapping,
} from "../../../api/customersApi"
import { ProductPickerTable } from "../../customer-requests/conformity/editor/ProductPickerTable"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"
import { McPill } from "../../shared/McPill"

/** Conformity v2 (Q8): the editable "products bought" mapping
 *  (mc_customer_products) — what the customer-scope declaration resolves its
 *  subjects from. Customer material number editable inline; sell-through
 *  products carry their pill. */
export function CustomerProductsBoughtSection() {
  const [searchParams] = useSearchParams()
  const customerId = searchParams.get("id")
  const { translate } = useTranslation()
  const mappings = useCustomerProductMappings(customerId)
  const upsert = useUpsertCustomerProductMapping()
  const remove = useDeleteCustomerProductMapping()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickedIds, setPickedIds] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(
    null,
  )

  const rows = mappings.data ?? []
  const mappedIds = new Set(rows.map((m) => m.productId))

  async function onAdd() {
    if (!customerId || upsert.isPending) return
    // Skip products already mapped — a bare re-PUT would wipe their stored
    // customer material number (review NF12).
    const newIds = [...pickedIds].filter((id) => !mappedIds.has(id))
    if (newIds.length === 0) {
      setPickerOpen(false)
      setPickedIds(new Set())
      return
    }
    const failedIds: string[] = []
    for (const productId of newIds) {
      try {
        await upsert.mutateAsync({
          customerId,
          productId,
          customerProductNumber: null,
        })
      } catch {
        failedIds.push(productId)
      }
    }
    setPickerOpen(false)
    if (failedIds.length === 0) {
      setPickedIds(new Set())
      return
    }
    setPickedIds(new Set(failedIds))
    const ok = newIds.length - failedIds.length
    if (ok > 0) {
      toast.success(
        translate("MC_MD_CUSTOMER_PRODUCTS_ADDED_PARTIAL", {
          ok,
          total: newIds.length,
        }),
      )
    } else {
      toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
    }
  }

  async function onSaveNumber(row: CustomerProductMapping, raw: string) {
    setEditing(null)
    if (!customerId) return
    const next = raw.trim() || null
    if (next === (row.customerProductNumber ?? null)) return
    try {
      await upsert.mutateAsync({
        customerId,
        productId: row.productId,
        customerProductNumber: next,
      })
    } catch {
      toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
    }
  }

  const columns: SubTableColumn<CustomerProductMapping>[] = [
    {
      key: "productNumber",
      header: translate("MC_MD_FIELD_PRODUCT_NUMBER"),
      cell: (m) => (
        <span className="flex items-center gap-2">
          <span className="font-mono">{m.productNumber}</span>
          {m.soldAsIs && (
            <McPill tone="default">
              {translate("MC_CS_CONF_V2_SELL_THROUGH_PILL")}
            </McPill>
          )}
        </span>
      ),
      className: "w-44",
    },
    {
      key: "name",
      header: translate("MC_MD_FIELD_NAME"),
      cell: (m) => <span className="block max-w-xs truncate">{m.name}</span>,
    },
    {
      key: "customerProductNumber",
      header: translate("MC_MD_CUST_PRODUCT_NUMBER_COL"),
      cell: (m) =>
        editing?.id === m.productId ? (
          <input
            autoFocus
            aria-label={translate("MC_MD_CUST_PRODUCT_NUMBER_COL")}
            value={editing.value}
            onChange={(e) =>
              setEditing({ id: m.productId, value: e.target.value })
            }
            onBlur={(e) => onSaveNumber(m, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveNumber(m, editing.value)
              if (e.key === "Escape") setEditing(null)
            }}
            className="font-mono h-6 w-full rounded border border-grey4 bg-grey3 px-1.5 text-[12px] text-white outline-hidden focus:border-yellow1"
          />
        ) : (
          <button
            type="button"
            onClick={() =>
              setEditing({
                id: m.productId,
                value: m.customerProductNumber ?? "",
              })
            }
            className="font-mono border-0 bg-transparent p-0 text-left text-white underline-offset-2 hover:underline"
          >
            {m.customerProductNumber ?? "—"}
          </button>
        ),
      className: "w-36",
    },
    {
      key: "remove",
      header: "",
      cell: (m) => (
        <button
          type="button"
          aria-label={translate("MC_CONF_V2_REMOVE_MAPPING", {
            number: m.productNumber,
          })}
          disabled={remove.isPending}
          onClick={() =>
            customerId &&
            remove
              .mutateAsync({ customerId, productId: m.productId })
              .catch(() => toast.error(translate("MC_CONF_V2_ACTION_FAILED")))
          }
          className="border-0 bg-transparent p-1 text-grey5 hover:text-white"
        >
          <X size={13} />
        </button>
      ),
      className: "w-10",
    },
  ]

  return (
    <>
      <EntitySubTable
        title={translate("MC_MD_CUST_PRODUCTS_BOUGHT")}
        items={rows}
        columns={columns}
        keyOf={(m) => m.productId}
        hasMore={false}
        onFetchMore={() => {}}
        isLoading={mappings.isLoading}
        total={rows.length}
        action={
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-[11.5px]"
            onClick={() => setPickerOpen(true)}
          >
            <Plus size={12} className="mr-1" />
            {translate("MC_MD_CUST_PRODUCTS_ADD")}
          </Button>
        }
      />

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{translate("MC_MD_CUST_PRODUCTS_ADD")}</DialogTitle>
          </DialogHeader>
          <ProductPickerTable
            selectedIds={pickedIds}
            onToggle={(id) =>
              setPickedIds((prev) => {
                const next = new Set(prev)
                if (next.has(id)) next.delete(id)
                else next.add(id)
                return next
              })
            }
          />
          <DialogFooter>
            <Button
              variant="outline"
              className="h-9 text-[13px]"
              onClick={() => {
                setPickerOpen(false)
                setPickedIds(new Set())
              }}
            >
              {translate("MC_CS_CONF_EDIT_CANCEL")}
            </Button>
            <Button
              className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
              disabled={pickedIds.size === 0 || upsert.isPending}
              onClick={onAdd}
            >
              {upsert.isPending && (
                <Loader2 size={13} className="mr-1.5 animate-spin" />
              )}
              {translate("MC_MD_CUST_PRODUCTS_ADD")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
